import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import '../scss/components/_ag-table.scss'
import { getServerSideDatasource } from '@revbox/helpers-fe'
import { searchPlaceholder } from '@revbox/localization'
import { changeStatus } from '../helpers/change-application-status'
import { useCookiesContext } from '../contexts/CookiesContext'
import { useDebouncedCallback } from '../hooks/useDebounceCallback'
const heightMarkers = ['logo_file'];

const getMenu = (params, url) => [
    {
        name: 'Open in New Window',
        action: () => {
            const id = params.node.data.id
            const urlWithId = `${url}?id=${id}`
            window.open(urlWithId, '_blank')
        }
    },
    'separator',
    'copy',
    'copyWithHeaders',
    'paste'
]

const defaultColDef = {
    flex: 1,
    minWidth: 90
}

export default React.memo(function BasicTable({
    getData,
    hiddenColumns,
    colWidth,
    rowFields,
    url,
    status,
    setCountByStatus,
    useCorrectionTypeMap,
    customColumns,
    additionalInfo,
    modalHandleSwitch,
    skin,
    filter,
    hasSearch = true
}) {
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])
    const gridStyle = useMemo(() => ({ minHeight: '200px', width: '100%' }), [])

    const [columnDefs, setColumnDefs] = useState([])
    const [search, setSearch] = useState('')
    const [gridApi, setGridApi] = useState(null)
    const [sortingParams, setSortingParams] = useState({
        columnName: null,
        sortOrder: null
    })

    const heightMarkersIncludes = rowFields.some(field => heightMarkers.includes(field))

    const rowHeight = useMemo(() => (heightMarkersIncludes ? 53 : null), [heightMarkersIncludes])

    const onFilterTextBoxChanged = useDebouncedCallback(e => setSearch(e.target.value), 500)
    const { getLanguageAbbr } = useCookiesContext();
    const language = getLanguageAbbr();
    const search_placeholder = searchPlaceholder[language];
    const onGridReady = useCallback(
        params => {
            setGridApi(params.api)
            const datasource = getServerSideDatasource({
                getData,
                hiddenColumns,
                colWidth,
                rowFields,
                setColumns: setColumnDefs,
                status,
                filter,
                useCorrectionTypeMap,
                setCountByStatus,
                search,
                customColumns,
                additionalInfo,
                language
            })

            params.api.setGridOption('serverSideDatasource', datasource)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [status]
    )

    const onRowClick = event => {
        const id = event.data.id
        const urlWithId = `${url}?id=${id}`
        const openInNewTab = event.event.metaKey || event.event.ctrlKey
        window.open(urlWithId, openInNewTab ? '_blank' : '_self')
    }

    const getContextMenuItems = params => {
        const menu = getMenu(params, url)
        const { client_application_status } = params.node.data
        if (!client_application_status) return menu
        if (client_application_status !== 'APPROVED') {
            changeStatus(client_application_status, params)
                const subMenus = ['REJECTED', 'APPROVED']
                menu.unshift({
                    name: 'Change Status',
                subMenu: subMenus.map(status => ({
                    name: status,
                    action: () => {
                        changeStatus(status, params)
                    }
                }))
            })
        }
        return menu
    }

    const onSortChanged = params => {
        setSortingParams({
            columnName: params.columns[0].colId,
            sortOrder: params.columns[0].sort
        })
    }

    useEffect(() => {
        if (gridApi) {
            const datasource = getServerSideDatasource({
                getData,
                hiddenColumns,
                colWidth,
                rowFields,
                setColumns: setColumnDefs,
                status,
                filter,
                setCountByStatus,
                useCorrectionTypeMap,
                search,
                customColumns,
                additionalInfo,
                sortingParams,
                language
            })

            gridApi.setGridOption('serverSideDatasource', datasource)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, gridApi, search, sortingParams])

    const inputRef = useRef(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    return (
        <div style={containerStyle}>
            <div style={gridStyle} className={skin === 'dark' ? 'ag-theme-quartz-dark ag-theme-quartz' : 'ag-theme-quartz-light ag-theme-quartz'}>
                { hasSearch && (
                    <div style={{ marginBottom: '10px' }}>
                        <input type="text" className="input-search" placeholder={search_placeholder} onInput={onFilterTextBoxChanged} ref={inputRef} />
                    </div>
                )}
                <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowModelType={'serverSide'}
                    rowHeight={rowHeight}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={false}
                    cacheBlockSize={10}
                    onGridReady={onGridReady}
                    onRowClicked={url ? onRowClick : modalHandleSwitch}
                    domLayout={'autoHeight'}
                    getContextMenuItems={getContextMenuItems}
                    onSortChanged={onSortChanged}
                />
            </div>
        </div>
    )
})
